import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RenderChartOutputSchema } from "@bliks-io/contracts";
import { CATEGORY_COLORS } from "../../../constants/category-colors";
import { CardContent, CardHeader, CardTitle } from "../../base/card/card";
import { Skeleton } from "../../base/skeleton/skeleton";
import { truncateText } from "../data-table/utils";
import { ToolPartBlock } from "./tool-part-block";
import { ChatPartCard, ChatPartNotice } from "./chat-part-shell";
/**
 * The chat's chart renderer (plan 009 §3). A chat agent never authors chart
 * code: it parameterizes a query, the backend recomputes the numbers, and this
 * trusted component draws the resulting `ChartSpec`.
 *
 * Colors are assigned HERE, never by the model — the spec carries none — so the
 * design system keeps ownership of the palette and an agent cannot pick an
 * unreadable one.
 */ const CHART_HEIGHT = 260;
/**
 * Top margin gives the highest Y tick label headroom; bottom clears the angled
 * X labels; the sides keep the outermost angled label off the container edge.
 * All four are stated because recharts takes `margin` as a whole object — an
 * omitted side is 0, not its default.
 */ const CHART_MARGIN = {
    top: 10,
    right: 8,
    bottom: 20,
    left: 8
};
/** Axis labels are drawn at an angle; longer than this and they overlap. */ const AXIS_LABEL_MAX_LENGTH = 20;
/**
 * Series palette, in order. Reusing the process-landscape colors keeps a chart
 * inside a chat visually part of the same product as the dashboard charts.
 *
 * Indexed off the map rather than listing the category NAMES: `check:translations`
 * harvests string literals out of UPPER_CASE arrays to expand template keys, and
 * a list of landscape category names here makes it expand
 * `processChains.categories.${…}` over values that field never takes.
 */ const SERIES_PALETTE = [
    CATEGORY_COLORS.core,
    CATEGORY_COLORS.management,
    CATEGORY_COLORS.support,
    CATEGORY_COLORS.uncategorized
];
function seriesColor(index) {
    return SERIES_PALETTE[index % SERIES_PALETTE.length].stroke;
}
/** The pale companion of `seriesColor`, for filled areas. */ function seriesFill(index) {
    return SERIES_PALETTE[index % SERIES_PALETTE.length].fill;
}
/**
 * Series values are keyed positionally rather than by series name: a name is
 * model-authored display text and could collide with `label` or with another
 * series, which would silently overwrite a row's data.
 */ function valueKey(seriesIndex) {
    return `v${seriesIndex}`;
}
/**
 * The backend's disambiguation scheme, applied to display text: the second
 * "Unknown" becomes "Unknown (2)". An ordinal needs no translation and keeps two
 * distinct groups distinguishable in the axis and the legend.
 */ function withOrdinalSuffix(label, occurrences) {
    const occurrence = (occurrences.get(label) ?? 0) + 1;
    occurrences.set(label, occurrence);
    return occurrence === 1 ? label : `${label} (${occurrence})`;
}
/**
 * The three cartesian families, each reduced to what actually differs: the
 * recharts container and how one series is drawn inside it. Everything else —
 * data, margin, axes, tooltip, legend — is identical, so it is written once in
 * `renderBody`.
 *
 * The series elements do NOT share a prop type (a corner `radius` is meaningless
 * on a line), which is why each family renders its own element rather than the
 * map carrying a bag of props a union of the three would have to accept.
 */ const CARTESIAN_CHARTS = {
    bar: {
        Container: BarChart,
        renderSeries: (series, seriesIndex)=>/*#__PURE__*/ _jsx(Bar, {
                dataKey: valueKey(seriesIndex),
                name: series.name,
                fill: seriesColor(seriesIndex),
                radius: [
                    4,
                    4,
                    0,
                    0
                ]
            }, valueKey(seriesIndex))
    },
    line: {
        Container: LineChart,
        renderSeries: (series, seriesIndex)=>/*#__PURE__*/ _jsx(Line, {
                type: "monotone",
                dataKey: valueKey(seriesIndex),
                name: series.name,
                stroke: seriesColor(seriesIndex),
                strokeWidth: 2,
                dot: false
            }, valueKey(seriesIndex))
    },
    area: {
        Container: AreaChart,
        renderSeries: (series, seriesIndex)=>/*#__PURE__*/ _jsx(Area, {
                type: "monotone",
                dataKey: valueKey(seriesIndex),
                name: series.name,
                stroke: seriesColor(seriesIndex),
                fill: seriesFill(seriesIndex)
            }, valueKey(seriesIndex))
    }
};
/**
 * Draws one chart spec. `pie` degrades to `bar` in two cases, because both would
 * otherwise misrepresent the data rather than merely look worse:
 *
 * - **more than one series** — a pie shows parts of ONE whole, and rendering
 *   only the first series would silently drop data the answer refers to;
 * - **more slices than the palette has colors** — slices are told apart by color
 *   alone, so a repeated color makes the legend unreadable. Bars carry their own
 *   axis labels and need no palette.
 */ function ChatChart({ spec, coverage }) {
    const { t, i18n } = useTranslation();
    const pointLabel = useMemo(()=>(point)=>point.labelKey ? t(`chat.chart.pointLabels.${point.labelKey}`) : point.label, [
        t
    ]);
    const formatValue = useMemo(()=>(value)=>formatChartValue(value, spec.unit, i18n.language, t), [
        spec.unit,
        i18n.language,
        t
    ]);
    /**
   * One row per point, merged across series.
   *
   * Rows are identified by the RAW `point.label`, which the backend has already
   * made unique. Translating first would undo that: several groups can share a
   * `labelKey` (every unreadable department is `unknown`), so two rows would
   * collapse into one and the later value would overwrite the earlier. The
   * translated text is what the reader sees, so it gets the same ordinal
   * treatment the backend applies to raw names.
   */ const rows = useMemo(()=>{
        const byRawLabel = new Map();
        const displayOccurrences = new Map();
        spec.series.forEach((series, seriesIndex)=>{
            series.data.forEach((point)=>{
                let row = byRawLabel.get(point.label);
                if (!row) {
                    const label = withOrdinalSuffix(pointLabel(point), displayOccurrences);
                    row = {
                        label,
                        axisLabel: truncateText(label, AXIS_LABEL_MAX_LENGTH)
                    };
                    byRawLabel.set(point.label, row);
                }
                row[valueKey(seriesIndex)] = point.value;
            });
        });
        return [
            ...byRawLabel.values()
        ];
    }, [
        spec.series,
        pointLabel
    ]);
    const pieWouldMisrepresent = spec.series.length > 1 || rows.length > SERIES_PALETTE.length;
    const chartType = spec.type === "pie" && pieWouldMisrepresent ? "bar" : spec.type;
    const showLegend = spec.series.length > 1;
    const tooltip = /*#__PURE__*/ _jsx(Tooltip, {
        content: ({ active, payload })=>{
            if (!active || !payload?.length) return null;
            const row = payload[0].payload;
            return /*#__PURE__*/ _jsxs("div", {
                className: "rounded-md border bg-popover px-3 py-2 text-sm shadow-md",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium",
                        children: row.label
                    }),
                    spec.series.map((series, seriesIndex)=>{
                        const value = row[valueKey(seriesIndex)];
                        if (typeof value !== "number") return null;
                        return /*#__PURE__*/ _jsxs("p", {
                            className: "text-muted-foreground",
                            children: [
                                showLegend ? `${series.name}: ` : "",
                                formatValue(value)
                            ]
                        }, valueKey(seriesIndex));
                    })
                ]
            });
        }
    });
    const cartesianAxes = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(CartesianGrid, {
                strokeDasharray: "3 3",
                vertical: false
            }),
            /*#__PURE__*/ _jsx(XAxis, {
                dataKey: "axisLabel",
                tick: {
                    fontSize: 12
                },
                interval: 0,
                angle: -30,
                textAnchor: "end",
                height: 60
            }),
            /*#__PURE__*/ _jsx(YAxis, {
                width: "auto",
                tick: {
                    fontSize: 12
                },
                tickFormatter: formatValue
            }),
            tooltip,
            showLegend && /*#__PURE__*/ _jsx(Legend, {})
        ]
    });
    function renderBody() {
        if (chartType === "pie") {
            return /*#__PURE__*/ _jsxs(PieChart, {
                margin: CHART_MARGIN,
                children: [
                    tooltip,
                    /*#__PURE__*/ _jsx(Pie, {
                        data: rows,
                        dataKey: valueKey(0),
                        nameKey: "label",
                        outerRadius: "80%",
                        label: false,
                        children: rows.map((row, rowIndex)=>/*#__PURE__*/ _jsx(Cell, {
                                fill: seriesColor(rowIndex)
                            }, row.label))
                    }),
                    /*#__PURE__*/ _jsx(Legend, {})
                ]
            });
        }
        const { Container, renderSeries } = CARTESIAN_CHARTS[chartType];
        return /*#__PURE__*/ _jsxs(Container, {
            data: rows,
            margin: CHART_MARGIN,
            children: [
                cartesianAxes,
                spec.series.map(renderSeries)
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(ChartCard, {
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                className: "pb-2",
                children: /*#__PURE__*/ _jsx(CardTitle, {
                    className: "text-base",
                    children: spec.title
                })
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                children: [
                    /*#__PURE__*/ _jsx(ResponsiveContainer, {
                        width: "100%",
                        height: CHART_HEIGHT,
                        children: renderBody()
                    }),
                    coverage && /*#__PURE__*/ _jsx(CoverageNote, {
                        coverage: coverage
                    })
                ]
            })
        ]
    });
}
/**
 * The shared part card, plus the one thing only a chart needs: recharts lays its
 * SVG out from a measured box and overshoots it during resize, so the overflow
 * is clipped here rather than in every part's shell.
 */ function ChartCard({ children, role, label }) {
    return /*#__PURE__*/ _jsx(ChatPartCard, {
        className: "overflow-hidden",
        role: role,
        label: label,
        children: children
    });
}
/**
 * What the query saw versus what the chart shows. A chart built under a
 * need-to-know principal's scope silently omits everything they may not read,
 * and a partial picture presented as complete is the failure mode this note
 * exists to prevent (plan 009 §6.8).
 */ function CoverageNote({ coverage }) {
    const { t } = useTranslation();
    // Both sentences, never the first that applies: scoping and the topN cap are
    // computed independently and routinely arrive together, so returning on the
    // first would leave a chart that is BOTH scoped down and cut off claiming
    // only the scoping — half of the very failure this note prevents.
    const notes = [
        coverage.omitted > 0 ? t("chat.chart.coverageOmitted", {
            omitted: coverage.omitted,
            requested: coverage.requested
        }) : null,
        coverage.truncated ? t("chat.chart.coverageTruncated", {
            rendered: coverage.rendered
        }) : null
    ].filter((note)=>note !== null);
    if (notes.length === 0) return null;
    return /*#__PURE__*/ _jsx("p", {
        className: "mt-2 text-xs text-muted-foreground",
        children: notes.join(" ")
    });
}
function formatChartValue(value, unit, language, t) {
    switch(unit){
        case "currency":
            return new Intl.NumberFormat(language, {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0
            }).format(value);
        case "hours":
            return t("chat.chart.hoursSuffix", {
                value: formatNumber(value, language)
            });
        case "percent":
            return t("chat.chart.percentSuffix", {
                value: formatNumber(value, language)
            });
        default:
            return formatNumber(value, language);
    }
}
function formatNumber(value, language) {
    return new Intl.NumberFormat(language, {
        maximumFractionDigits: 1
    }).format(value);
}
/** Placeholder while the model is still assembling the chart's parameters. */ function ChatChartSkeleton() {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsxs(ChartCard, {
        role: "status",
        label: t("chat.chart.loading"),
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                className: "pb-2",
                children: /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-4 w-48"
                })
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                className: "flex items-end gap-3",
                style: {
                    height: CHART_HEIGHT
                },
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-1/3 w-full"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-2/3 w-full"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-1/2 w-full"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-3/4 w-full"
                    })
                ]
            })
        ]
    });
}
/**
 * A chart that could not be produced or whose payload does not hold. Nothing is
 * persisted for it, so it disappears on reload (plan 009 §6.9).
 */ function ChatChartError() {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsx(ChatPartNotice, {
        message: t("chat.chart.failed")
    });
}
/** Draws whatever state a `renderChart` part is in. */ export function ChatChartBlock({ part }) {
    return /*#__PURE__*/ _jsx(ToolPartBlock, {
        part: part,
        schema: RenderChartOutputSchema,
        skeleton: /*#__PURE__*/ _jsx(ChatChartSkeleton, {}),
        error: /*#__PURE__*/ _jsx(ChatChartError, {}),
        children: (output)=>/*#__PURE__*/ _jsx(ChatChart, {
                spec: output.spec,
                coverage: output.coverage
            })
    });
}

//# sourceMappingURL=chat-chart.js.map